import React from 'react';
import {
  AppBar,
  Toolbar,
  Button,
  Box,
  IconButton,
  Drawer,
  List,
  ListItem,
  ListItemText,
  ListItemButton,
  Popper,
  Paper,
  MenuList,
  MenuItem,
  Grid,
  Typography,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import CloseIcon from '@mui/icons-material/Close';
import { alpha } from '@mui/material/styles';
import { Link, useLocation } from 'react-router-dom';
import logoDark from '../assets/logo-dark.svg';

const pages = [
  { label: 'Home', path: '/' },
  { label: 'About', path: '/about' },
  { label: 'Services', path: '/services' },
  { label: 'Portfolio', path: '/portfolio' },
  { label: 'Pricing', path: '/pricing' },
  //{ label: 'Team', path: '/team' },
  { label: 'Blog', path: '/blog' },
  { label: 'Partners', path: '/partners' },
  { label: 'Industries', path: '/industries' },
  { label: 'Careers', path: '/careers' },
  { label: 'Contact', path: '/contact' }
];

export default function Navbar() {
  const [open, setOpen] = React.useState(false);
  const location = useLocation();

  // Mega-menu state
  const [menuAnchor, setMenuAnchor] = React.useState(null);
  const [menuId, setMenuId] = React.useState(null);

  const megaMenuData = {
    Services: [
      { title: 'Custom Software', path: '/services/custom-software' },
      { title: 'Web & Mobile Apps', path: '/services/web-mobile' },
      { title: 'Cloud & DevOps', path: '/services/cloud-devops' },
      { title: 'UI / UX Design', path: '/services/ui-ux' }
    ],
    Portfolio: [
      { title: 'Sentiment Analysis', path: '/portfolio/sentiment-analysis' },
      { title: 'Predictive Analytics', path: '/portfolio/predictive-analytics' },
      { title: 'Computer Vision', path: '/portfolio/computer-vision' }
    ]
    ,
    Partners: [
      { title: 'Technology Partners', path: '/partners' },
      { title: 'Business Partners', path: '/partners' }
    ],
    Industries: [
      { title: 'Healthcare', path: '/industries#healthcare' },
      { title: 'Finance', path: '/industries#finance' },
      { title: 'Retail', path: '/industries#retail' },
      { title: 'Manufacturing', path: '/industries#manufacturing' },
      { title: 'Local Bar Associations', path: '/industries#local-bar-associations' },
      { title: 'Digital Marketing', path: '/industries#digital-marketing' }
    ]
  };

  const openMegaMenu = (id, event) => {
    clearCloseTimer();
    setMenuId(id);
    setMenuAnchor(event.currentTarget);
  };

  // When a mega-menu opens, move focus to the first menuitem for keyboard users
  React.useEffect(() => {
    if (menuId && menuAnchor) {
      // wait for Popper to render
      const t = setTimeout(() => {
        const menu = document.getElementById(`${menuId}-menu`);
        if (menu) {
          const items = menu.querySelectorAll('[role="menuitem"]');
          if (items && items.length > 0) {
            (items[0]).focus();
          }
        }
      }, 50);
      return () => clearTimeout(t);
    }
    return undefined;
  }, [menuId, menuAnchor]);

  // Keyboard navigation inside the mega-menu: arrows move between menuitems
  const handleMegaMenuKeyDown = (e) => {
    if (!menuId) return;
    const menu = document.getElementById(`${menuId}-menu`);
    if (!menu) return;
    const items = Array.from(menu.querySelectorAll('[role="menuitem"]'));
    if (!items.length) return;
    const active = document.activeElement;
    const idx = items.indexOf(active);
    if (e.key === 'ArrowRight' || e.key === 'ArrowDown') {
      e.preventDefault();
      const next = items[(idx + 1) % items.length];
      next && next.focus();
    } else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') {
      e.preventDefault();
      const prev = items[(idx - 1 + items.length) % items.length];
      prev && prev.focus();
    } else if (e.key === 'Escape') {
      closeMegaMenu();
    }
  };

  const closeMegaMenu = () => {
    clearCloseTimer();
    setMenuId(null);
    setMenuAnchor(null);
  };

  const closeTimerRef = React.useRef();
  const clearCloseTimer = () => {
    if (closeTimerRef.current) {
      clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
  };
  const scheduleClose = (delay = 150) => {
    clearCloseTimer();
    closeTimerRef.current = setTimeout(() => {
      setMenuId(null);
      setMenuAnchor(null);
      closeTimerRef.current = null;
    }, delay);
  };

  const isActivePath = (p) => {
    if (p === '/') return location.pathname === '/';
    return location.pathname === p || location.pathname.startsWith(p + '/') || location.pathname.startsWith(p);
  };

  return (
    <AppBar position="static" color="transparent" elevation={0} sx={{ backdropFilter: 'blur(6px)' }}>
      <Toolbar sx={{ flexWrap: 'nowrap' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', flexGrow: 1 }}>
          <img src={logoDark} alt="NehaInfoTech" style={{ height: 44, marginRight: 12 }} />
          <Box sx={{ display: { xs: 'none', md: 'flex' }, alignItems: 'center', flexWrap: 'nowrap', whiteSpace: 'nowrap' }}>
            {pages.map(page => {
              const hasMega = ['Services', 'Portfolio'].includes(page.label);
              const active = isActivePath(page.path);
              return (
                <Box key={page.path} component="span" sx={{ mx: 0.2, display: 'inline-flex' }}>
                  <Button
                    color={active ? 'primary' : 'inherit'}
                    component={Link}
                    to={page.path}
                        onMouseEnter={hasMega ? (e) => openMegaMenu(page.label, e) : undefined}
                        onFocus={hasMega ? (e) => openMegaMenu(page.label, e) : undefined}
                        onMouseLeave={hasMega ? () => scheduleClose() : undefined}
                        aria-haspopup={hasMega ? 'true' : undefined}
                        aria-expanded={hasMega && menuId === page.label ? 'true' : undefined}
                        aria-controls={hasMega && menuId === page.label ? `${page.label}-menu` : undefined}
                        sx={(theme) => ({
                      mx: 1,
                      fontWeight: active ? 700 : 500,
                      '&:focus-visible': {
                        boxShadow: `0 0 0 4px ${alpha(theme.palette.primary.main, 0.24)}`,
                        borderRadius: 4
                      }
                    })}
                  >
                    {page.label}
                  </Button>
                </Box>
              );
            })}
          </Box>
          <IconButton sx={{ display: { xs: 'inline-flex', md: 'none' } }} onClick={() => setOpen(true)}>
            <MenuIcon />
          </IconButton>
        </Box>
      </Toolbar>
      <Drawer
        anchor="left"
        open={open}
        onClose={() => setOpen(false)}
        ModalProps={{ keepMounted: true, disableAutoFocus: false, disableEnforceFocus: false }}
      >
        <Box sx={{ width: 300, p: 1 }} role="presentation">
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', px: 1 }}>
            <IconButton onClick={() => setOpen(false)} aria-label="Close navigation">
              <CloseIcon />
            </IconButton>
          </Box>
          <List>
            {pages.map(p => (
              <ListItem key={p.path} disablePadding>
                <ListItemButton
                  component={Link}
                  to={p.path}
                  onClick={() => setOpen(false)}
                  selected={isActivePath(p.path)}
                >
                  <ListItemText primary={p.label} />
                </ListItemButton>
              </ListItem>
            ))}
          </List>
        </Box>
      </Drawer>

      {/* Mega-menu popper (desktop only) */}
      <Popper
        id={menuId ? `${menuId}-popper` : undefined}
        open={Boolean(menuAnchor) && Boolean(menuId)}
        anchorEl={menuAnchor}
        placement="bottom-start"
        disablePortal={false}
        modifiers={[{ name: 'offset', options: { offset: [0, 6] } }]}
        sx={{ zIndex: (theme) => theme.zIndex.appBar + 1 }}
      >
        {menuId && (
          <Paper elevation={4} onMouseEnter={clearCloseTimer} onMouseLeave={() => scheduleClose()} onKeyDown={handleMegaMenuKeyDown} role="menu" aria-label={`${menuId} menu`}>
            <Box sx={{ p: 2, minWidth: 420 }}>
              <Grid container spacing={2}>
                {(megaMenuData[menuId] || []).reduce((rows, item, idx) => {
                  // create 2-column layout
                  const col = Math.floor(idx / 2);
                  rows[col] = rows[col] || [];
                  rows[col].push(item);
                  return rows;
                }, []).map((colItems, colIdx) => (
                  <Grid item xs={6} key={colIdx}>
                    <MenuList id={`${menuId}-menu`}>
                      {colItems.map(mi => (
                        <MenuItem key={mi.path} component={Link} to={mi.path} onClick={() => { closeMegaMenu(); }} role="menuitem">
                          <Typography variant="body1">{mi.title}</Typography>
                        </MenuItem>
                      ))}
                    </MenuList>
                  </Grid>
                ))}
              </Grid>
            </Box>
          </Paper>
        )}
      </Popper>
    </AppBar>
  );
}
